import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Location, NavigateFunction } from 'react-router-dom';
import type { ListingPageResult } from '@/features/plp/domain/models/ProductListing';

interface BrowsingHistoryCategoriesResponse {
  categories: string[];
}

interface BrowsingHistoryServiceInterface {
  getListingPage(
    category: string | undefined,
    pageNumber: number,
    opts?: { signal?: AbortSignal },
  ): Promise<ListingPageResult>;
  getCategories(): Promise<BrowsingHistoryCategoriesResponse>;
}

export interface UseBrowsingHistoryOptions {
  service: BrowsingHistoryServiceInterface;
  location: Location;
  navigate: NavigateFunction;
}

export interface UseBrowsingHistoryReturn {
  categories: string[];
  activeCategory: string;
  onCategoryClick: (category: string) => void;
  listingKey: string;
  fetchPage: (pageNumber: number, opts?: { signal?: AbortSignal }) => Promise<ListingPageResult>;
  apiCategory: string | undefined;
}

const RECENTLY_VIEWED_PATH = '/recently-viewed';

function getCategoryFromSearch(search: string): string {
  const raw = new URLSearchParams(search).get('category')?.trim().toLowerCase();
  return raw || 'all';
}

function getApiCategoryFromActive(activeCategory: string): string | undefined {
  if (!activeCategory || activeCategory === 'all') return undefined;
  return activeCategory;
}

export function useBrowsingHistory({
  service,
  location,
  navigate,
}: UseBrowsingHistoryOptions): UseBrowsingHistoryReturn {
  const activeCategory = useMemo(
    () => getCategoryFromSearch(location.search),
    [location.search],
  );
  const apiCategory = useMemo(() => getApiCategoryFromActive(activeCategory), [activeCategory]);
  const listingKey = useMemo(
    () => `${RECENTLY_VIEWED_PATH}${location.search || ''}`,
    [location.search],
  );

  const [categories, setCategories] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    service
      .getCategories()
      .then((result) => {
        if (!cancelled) setCategories(result.categories ?? []);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [service]);

  const onCategoryClick = useCallback(
    (category: string) => {
      const normalized = category.toLowerCase();
      const params = new URLSearchParams();
      if (normalized && normalized !== 'all') {
        params.set('category', normalized);
      }
      const search = params.toString();
      navigate(
        {
          pathname: RECENTLY_VIEWED_PATH,
          search: search ? `?${search}` : '',
        },
        { replace: true },
      );
    },
    [navigate],
  );

  const fetchPage = useCallback(
    (pageNumber: number, opts?: { signal?: AbortSignal }) =>
      service.getListingPage(apiCategory, pageNumber, opts),
    [service, apiCategory],
  );

  return {
    categories,
    activeCategory,
    onCategoryClick,
    listingKey,
    fetchPage,
    apiCategory,
  };
}
